import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Box, Button, Typography } from "@mui/material";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import ShieldRoundedIcon from "@mui/icons-material/ShieldRounded";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import FavoriteRoundedIcon from "@mui/icons-material/FavoriteRounded";
import StarRoundedIcon from "@mui/icons-material/StarRounded";

export default function HeroSection() {
  return (
    <Box
      component="section"
      sx={{
        position: "relative",
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        overflow: "hidden"
      }}
    >
      {/* Background */}
      <Box className="wellness-bg" sx={{ position: "absolute", inset: 0 }} />
      <Box
        sx={{
          position: "absolute",
          top: 80,
          right: 0,
          width: 600,
          height: 600,
          bgcolor: "rgba(39,165,153,0.05)",
          borderRadius: "50%",
          filter: "blur(64px)"
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: 0,
          left: 0,
          width: 400,
          height: 400,
          bgcolor: "rgba(57,172,130,0.05)",
          borderRadius: "50%",
          filter: "blur(64px)"
        }}
      />

      {/* Floating cards */}
      <motion.div
        animate={{ y: [0, -20, 0] }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
        style={{ position: "absolute", top: 160, right: 80, display: "none" }}
        className="hero-float-1"
      >
        <Box className="glass-card" sx={{ p: 2, display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box sx={{ width: 48, height: 48, borderRadius: "50%", bgcolor: "rgba(57,172,130,0.2)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <ShieldRoundedIcon sx={{ color: "#39ac82" }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: 14 }}>24/7 Care</Typography>
            <Typography sx={{ fontSize: 12, color: "text.secondary" }}>Always available</Typography>
          </Box>
        </Box>
      </motion.div>

      <motion.div
        animate={{ y: [0, 15, 0] }}
        transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
        style={{ position: "absolute", bottom: 160, right: 160, display: "none" }}
        className="hero-float-2"
      >
        <Box className="glass-card" sx={{ p: 2, display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box sx={{ width: 48, height: 48, borderRadius: "50%", bgcolor: "rgba(39,165,153,0.2)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <StarRoundedIcon sx={{ color: "primary.main" }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: 14 }}>4.9 Rating</Typography>
            <Typography sx={{ fontSize: 12, color: "text.secondary" }}>10,000+ patients</Typography>
          </Box>
        </Box>
      </motion.div>

      <Box className="container-custom" sx={{ position: "relative", zIndex: 1, pt: "128px", pb: "80px" }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
            gap: "48px",
            alignItems: "center"
          }}
        >
          {/* Content */}
          <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: "easeOut" }}>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                padding: "8px 16px",
                borderRadius: 999,
                background: "rgba(39,165,153,0.1)",
                color: "#27a599",
                fontSize: 14,
                fontWeight: 600,
                marginBottom: 24
              }}
            >
              <FavoriteRoundedIcon sx={{ fontSize: 16 }} />
              <span>Trusted by 50,000+ Families</span>
            </motion.div>

            <Typography
              component="h1"
              sx={{
                fontSize: { xs: "2.25rem", md: "3rem", lg: "3.75rem" },
                fontWeight: 800,
                lineHeight: 1.1,
                mb: 3
              }}
            >
              Personalized
              <Box component="span" className="gradient-text" sx={{ display: "block" }}>
                Concierge Care
              </Box>
              for Total Wellness
            </Typography>

            <Typography sx={{ fontSize: "1.125rem", color: "text.secondary", mb: 4, maxWidth: 520 }}>
              Experience healthcare reimagined. 24/7 access to your personal doctor, holistic
              wellness programs, and preventive care that puts you first.
            </Typography>

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 4, color: "text.secondary" }}>
              {[
                { icon: <AccessTimeRoundedIcon sx={{ fontSize: 16, color: "primary.main" }} />, text: "24/7 Virtual Access" },
                { icon: <ShieldRoundedIcon sx={{ fontSize: 16, color: "primary.main" }} />, text: "Concierge Doctors" },
                { icon: <FavoriteRoundedIcon sx={{ fontSize: 16, color: "primary.main" }} />, text: "Holistic Care" }
              ].map((f) => (
                <Box key={f.text} sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: 14 }}>
                  {f.icon}
                  <span>{f.text}</span>
                </Box>
              ))}
            </Box>

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
              <Button
                component={Link}
                to="/signup"
                size="large"
                variant="contained"
                sx={{
                  bgcolor: "primary.main",
                  color: "#fff",
                  boxShadow: "var(--shadow-glow)",
                  "&:hover": { bgcolor: "rgba(39,165,153,0.9)" }
                }}
                endIcon={<ArrowForwardRoundedIcon sx={{ transition: "transform 200ms" }} />}
              >
                Get Started
              </Button>

              <Button
                component={Link}
                to="/services"
                size="large"
                variant="outlined"
                sx={{
                  borderColor: "hsla(200, 20%, 90%, 1)",
                  color: "text.primary"
                }}
                startIcon={<PlayArrowRoundedIcon sx={{ transition: "transform 200ms" }} />}
              >
                Explore Services
              </Button>
            </Box>
          </motion.div>

          {/* Right illustration */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            style={{ display: "none" }}
            className="hero-right"
          >
            <Box sx={{ position: "relative" }}>
              <Box className="glass-card" sx={{ p: 4, borderRadius: "24px" }}>
                <Box
                  sx={{
                    aspectRatio: "1 / 1",
                    borderRadius: "16px",
                    background: "linear-gradient(135deg, rgba(39,165,153,0.2) 0%, rgba(57,172,130,0.2) 50%, hsl(var(--accent)) 100%)",
                    overflow: "hidden",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                  }}
                >
                  <Box sx={{ textAlign: "center", p: 4 }}>
                    <Box sx={{ width: 128, height: 128, mx: "auto", mb: 3, borderRadius: "50%", bgcolor: "rgba(39,165,153,0.2)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <FavoriteRoundedIcon sx={{ fontSize: 64, color: "primary.main" }} />
                    </Box>
                    <Typography sx={{ fontSize: 24, fontWeight: 800, mb: 1 }}>Your Health Partner</Typography>
                    <Typography sx={{ color: "text.secondary" }}>Personalized care at your fingertips</Typography>
                  </Box>
                </Box>
              </Box>

              <motion.div
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 4, repeat: Infinity }}
                style={{ position: "absolute", bottom: -24, left: -24 }}
              >
                <Box className="glass-card" sx={{ p: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
                    <Box sx={{ textAlign: "center" }}>
                      <Typography sx={{ fontSize: 24, fontWeight: 800, color: "primary.main" }}>50K+</Typography>
                      <Typography sx={{ fontSize: 12, color: "text.secondary" }}>Happy Patients</Typography>
                    </Box>
                    <Box sx={{ width: 1, height: 40, bgcolor: "hsl(var(--border))" }} />
                    <Box sx={{ textAlign: "center" }}>
                      <Typography sx={{ fontSize: 24, fontWeight: 800, color: "#39ac82" }}>200+</Typography>
                      <Typography sx={{ fontSize: 12, color: "text.secondary" }}>Expert Doctors</Typography>
                    </Box>
                  </Box>
                </Box>
              </motion.div>
            </Box>
          </motion.div>
        </Box>
      </Box>
    </Box>
  );
}
